import {isNil, not} from 'ramda'
import { ConsoleLogger } from 'aws-amplify/utils'
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  confirmSignIn,
  confirmSignUp as amplifyConfirmSignUp,
  resetPassword,
  confirmResetPassword,
  updatePassword,
  getCurrentUser,
} from 'aws-amplify/auth'
import Result from 'folktale/result'
import * as Api from 'lib/api'

const logger = new ConsoleLogger('Auth')

export const newPasswordRequired = (result) =>
  result && result.nextStep && result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'

export class NewPasswordRequired {
  constructor(user) {
    this.user = user
  }
}

export class UserNotConfirmed extends Error {
}

export class PasswordResetRequired extends Error {
}

export class NotAuthorized extends Error {
}

export class UserNotFound extends Error {
}

export class UnknownFailure extends Error {
}

export class CodeMismatch extends Error {
}

export class ExpiredCode extends Error {
}

export const signIn = async (username, password) => {
  try {
    const result = await amplifySignIn({ username, password })
    if (newPasswordRequired(result)) {
      return Result.Error(new NewPasswordRequired(result))
    } else {
      return Result.Ok(result)
    }
  } catch (err) {
    if (err.name === 'UserNotConfirmedException') {
      return Result.Error(new UserNotConfirmed(err.name))
    } else if (err.name === 'PasswordResetRequiredException') {
      return Result.Error(new PasswordResetRequired(err.name))
    } else if (err.name === 'NotAuthorizedException') {
      return Result.Error(new NotAuthorized(err.name))
    } else if (err.name === 'UserNotFoundException') {
      return Result.Error(new UserNotFound(err.name))
    }
    logger.info(err)
    return Result.Error(new UnknownFailure(err))
  }
}

export const forgotPassword = async (username) => {
  try {
    const response = await resetPassword({ username })
    logger.info(response)
    return Result.Ok(response)
  } catch (err) {
    if (err.name === 'UserNotFoundException') {
      return Result.Error(new UserNotFound(err.name))
    }
    logger.info(err)
    return Result.Error(new UnknownFailure(err))
  }
}

export const forgotPasswordSubmit = async (username, code, newPassword) => {
  try {
    const response = await confirmResetPassword({ username, confirmationCode: code, newPassword })
    logger.info(response)
    return Result.Ok(response)
  } catch (err) {
    if (err.name === 'CodeMismatchException') {
      return Result.Error(new CodeMismatch(err.name))
    }
    if (err.name === 'ExpiredCodeException') {
      return Result.Error(new ExpiredCode(err.name))
    }
    logger.info(err)
    return Result.Error(new UnknownFailure(err))
  }
}

export async function changePassword(user, oldPassword, newPassword) {
  try {
    const response = await updatePassword({ oldPassword, newPassword })
    logger.info(response)
    return Result.Ok(response)
  } catch(err) {
    return Result.Error(new NotAuthorized('NotAuthorizedException'))
  }
}

export const signOut = async () => {
  const user = await currentAuthenticatedUser()
  removeTenantFromStorage(user)
  await amplifySignOut()
}

export const currentAuthenticatedUser = async () => {
  return await getCurrentUser()
}

export const confirmSignUp = async (username, code) => {
  return await amplifyConfirmSignUp({ username, confirmationCode: code })
}

export const currentTenant = (user) => {
  const namespace = `${process.env.REACT_APP_CLIENT_ID}.${user.username}.tenant`
  const tenant = localStorage.getItem(namespace)
  return JSON.parse(tenant)
}

export const switchTenant = (user, tenant) => {
  if (not(isNil(tenant))) {
    saveTenantToLocalStorage(user, tenant)
  } else {
    logger.info('didn\'t find any tenant...')
  }
}

function saveTenantToLocalStorage(user, tenant) {
  const namespace = `${process.env.REACT_APP_CLIENT_ID}.${user.username}.tenant`
  logger.info(`saving tenant to local storage '${namespace}'`)
  localStorage.setItem(namespace, JSON.stringify({id: tenant.id}))
}

function removeTenantFromStorage(user) {
  const namespace = `${process.env.REACT_APP_CLIENT_ID}.${user.username}.tenant`
  logger.info(`removing tenant to local storage '${namespace}'`)
  localStorage.removeItem(namespace)
}

async function completeNewPassword(user, newPassword) {
  const result = await confirmSignIn({ challengeResponse: newPassword })
  const tenant = await Api.getTenant()
  switchTenant(result, tenant)
  return result
}

const Auth = {
  newPasswordRequired,
  signIn,
  confirmSignUp,
  completeNewPassword,
  currentAuthenticatedUser,
  currentTenant,
  switchTenant,
  signOut,
  forgotPassword,
  forgotPasswordSubmit,
  changePassword
}

export default Auth
